import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { memberHandler } from '../../redux/handlers';
import { useParams } from 'react-router-dom';
import { nanoid } from 'nanoid';

export default function PayDebtors({ order, actions, payment, settings, orderData, ...props }) {
  const dispatch = useDispatch();
  const { businessSlug } = useParams();
  const { details: orderDetails } = order;
  const { addDebtorPayment } = actions;
  const { setSelectedMethod } = payment;
  const { refreshList } = settings || {};
  const currentMember = useSelector(state => state.member?.individualData);
  const [customerSelect, setCustomerSelect] = useState(null);
  const [newDebt, setNewDebt] = useState('0');

  useEffect(() => {
    if (orderData && orderData.memberID) {
      dispatch(memberHandler.getMemberById(businessSlug, orderData.memberID));
    }
  }, [dispatch, businessSlug, orderData]);

  useEffect(() => {
    if (currentMember && orderData?.memberID) {
      setCustomerSelect({
        id: currentMember.id,
        fullName: `${currentMember.first_name_th || ''} ${currentMember.last_name_th || ''}`.trim(),
        code: currentMember.code,
        tel: currentMember.phone_number,
        point: currentMember.point || 0,
        debt: currentMember.total_debt || 0,
        creditLimit: currentMember.credit_limit_amount || 0,
        pointExpire: '31/12/2566'
      });

      setNewDebt('0');
    }
  }, [currentMember, orderData?.memberID]);

  const handleDebtChange = e => {
    let value = e.target.value;

    if (!customerSelect) {
      setNewDebt('0');
      return;
    }

    if (value === '') {
      setNewDebt('0');
      return;
    }

    if (!/^[0-9]*\.?[0-9]*$/.test(value)) {
      return;
    }

    if (value.length > 1 && value[0] === '0' && value[1] !== '.') {
      value = value.replace(/^0+/, '');
      if (value === '') {
        value = '0';
      }
    }

    if (parseFloat(value) < 0) {
      setNewDebt('0');
      return;
    }

    const netPaymentAmount = parseFloat(orderDetails?.paymentNet || '0');
    const creditLimitVal = parseFloat(customerSelect?.creditLimit) || 0;
    const existingDebtVal = parseFloat(customerSelect?.debt) || 0;
    const remainingCreditVal = Math.max(creditLimitVal - existingDebtVal, 0);
    const maxAllowed = Math.min(netPaymentAmount, remainingCreditVal);

    if (parseFloat(value) > maxAllowed) {
      setNewDebt(String(maxAllowed));
      return;
    }

    setNewDebt(value);
  };

  const handleAddDebt = useCallback(() => {
    if (!newDebt || !customerSelect || parseFloat(newDebt) <= 0) return;
    const debtAmount = parseFloat(newDebt).toFixed(2);

    const newPaymentItem = {
      id: nanoid(),
      channel: {
        id: payment.selectedMethod.paymentChannelID,
        paymentChannelName: 'บันทึกลูกหนี้'
      },
      amount: debtAmount,
      provider: customerSelect,
      remark: '',
      status: 1,
      is_debt: false,
      member_id: customerSelect.id,
      debt_amount: debtAmount
    };

    addDebtorPayment && addDebtorPayment(newPaymentItem);

    if (setSelectedMethod) {
      setSelectedMethod({
        paymentChannelID: '1',
        paymentChannelName: 'เงินสด',
        icon: 'cash',
        code: 'CASH'
      });

      if (typeof refreshList === 'function') {
        refreshList();
      }
    }

    setNewDebt('0');
  }, [newDebt, customerSelect, payment, addDebtorPayment, setSelectedMethod, refreshList]);

  const calculateTotalDebt = useCallback(() => {
    if (!customerSelect) return 0;

    const existingDebt = parseFloat(customerSelect.debt) || 0;
    const newDebtValue = parseFloat(newDebt) || 0;

    return existingDebt + newDebtValue;
  }, [customerSelect, newDebt]);

  const orderNo = orderDetails?.orderNo || '-';
  const netPaymentAmount = orderDetails?.paymentNet || '0';

  const creditLimit = parseFloat(customerSelect?.creditLimit) || 0;
  const existingDebt = parseFloat(customerSelect?.debt) || 0;
  const remainingCredit = Math.max(creditLimit - existingDebt, 0);
  const remainingCreditAfterNewDebt = Math.max(remainingCredit - (parseFloat(newDebt) || 0), 0);
  const isConfirmDisabled =
    !customerSelect || parseFloat(newDebt) <= 0 || parseFloat(newDebt) > parseFloat(netPaymentAmount) || parseFloat(newDebt) > remainingCredit;

  return (
    <div className="h-100 d-flex flex-column">
      <div className="d-flex align-items-center text-[#061126] mb-3">
        <div className="me-auto">
          <span className="fs-18 fw-500">Order NO.</span>
          <span className="fs-14 fw-500 ms-2">{orderNo}</span>
        </div>
        <div className="fs-18 fw-bold text-[#2DC76D]">บันทึกลูกหนี้</div>
      </div>

      <div className="fs-16 fw-500 mb-2 text-[#2DC76D]">ข้อมูลลูกค้า</div>

      <div
        className="p-4 text-[#2F4858] mb-4"
        style={{
          borderRadius: '15px',
          background: 'rgba(223, 247, 233, 0.15)'
        }}
      >
        <div className="d-flex align-items-center mb-4">
          <div className="me-auto">
            <i className="icon-person-c me-2"></i>
            <span className="py-3">รหัสลูกค้า</span>
          </div>
          <div>{customerSelect?.code || '-'}</div>
        </div>

        <div className="d-flex align-items-center mb-4">
          <div className="me-auto">
            <i className="icon-card me-2"></i>
            <span className="py-3">ชื่อลูกค้า</span>
          </div>
          <div>{customerSelect?.fullName || '-'}</div>
        </div>

        <div className="d-flex align-items-center mb-4">
          <div className="me-auto">
            <i className="icon-tel me-2"></i>
            <span className="py-3">เบอร์โทรศัพท์</span>
          </div>
          <div>{customerSelect?.tel || '-'}</div>
        </div>
      </div>

      <div className="mb-3">
        <div className="row">
          <div className="col-6">
            <div className="text-[#2DC76D] font-bold text-[14px] mb-1">ยอดหนี้ใหม่ (บาท)</div>
            <input
              type="text"
              className="border-0 form-st-6 text-end text-[#2DC76D]"
              value={newDebt}
              onChange={handleDebtChange}
              disabled={!customerSelect?.id}
              inputMode="decimal"
              maxLength={10}
            />
          </div>
          <div className="col-6">
            <div className="text-[#2DC76D] font-bold text-[14px] mb-1">วงเงินคงเหลือ (บาท)</div>
            <input
              type="text"
              inputMode="decimal"
              className="border-0 form-st-6 text-end text-[#2DC76D]"
              value={remainingCreditAfterNewDebt}
              disabled
            />
          </div>
        </div>
      </div>

      <div>
        <div className="row">
          <div className="col-6">
            <div className="text-[#2DC76D] font-bold text-[14px] mb-2">ยอดหนี้คงค้างเดิม (บาท)</div>
            <input
              type="text"
              inputMode="decimal"
              className="border-0 form-st-6 text-end text-[#2DC76D]"
              value={customerSelect?.debt || 0}
              disabled
            />
          </div>

          <div className="col-6">
            <div className="text-[#2DC76D] font-bold text-[14px] mb-2">ยอดหนี้สุทธิ (บาท)</div>
            <input
              type="text"
              inputMode="decimal"
              className="border-0 form-st-6 text-end text-[#2DC76D]"
              value={calculateTotalDebt()}
              disabled
            />
          </div>
        </div>
      </div>

      <div className="d-flex pt-2 mt-auto">
        <button className="ms-auto btn btn-st-q1 px-5" onClick={handleAddDebt} disabled={isConfirmDisabled}>
          ยืนยัน
        </button>
      </div>
    </div>
  );
}
